/**
 * Generic Data Adapter
 *
 * Reusable data adapter for components with list + detail + pricing architecture.
 * Merges data from three sources:
 * 1. List files (basic info: manufacturer, model, specs)
 * 2. Detail files (additional specs, optional)
 * 3. Pricing files (regularPrice, currentPrice)
 */

export interface PriceInfo {
  regularPrice?: number | null
  currentPrice?: number | null
  trend?: 'up' | 'down' | 'stable'
  availability?: 'in-stock' | 'out-of-stock'
  lastChecked?: string
  asin?: string
}

export interface PricingData {
  lastUpdated: string
  ttl: number
  prices: { [id: string]: PriceInfo }
}

// Note: This interface is intentionally minimal to allow specific component models
// to be used without requiring an explicit index signature
export interface ComponentModel {
  id: string
  status: string
}

export interface GenericDataAdapterConfig {
  basePath: string
  componentName: string // e.g., 'psu', 'cooler', 'memory'
  pricingTTL?: number
  listTTL?: number
}

export class GenericDataAdapter<T extends ComponentModel> {
  private readonly basePath: string
  private readonly componentName: string
  private detailCache: Map<string, Partial<T>> = new Map()
  private listCache: Map<string, T> = new Map()
  private currentIds: Set<string> = new Set()
  private discontinuedIds: Set<string> = new Set()
  private listLoadTime: number = 0
  private pricingCache: Map<string, PriceInfo> = new Map()
  private pricingLoadTime: number = 0
  private readonly PRICING_TTL: number
  private readonly LIST_TTL: number

  constructor(config: GenericDataAdapterConfig) {
    this.basePath = config.basePath
    this.componentName = config.componentName
    this.PRICING_TTL = config.pricingTTL ?? 300000 // 5 minutes
    this.LIST_TTL = config.listTTL ?? 600000 // 10 minutes
  }

  /**
   * Get detail data for a specific item (with pricing)
   */
  async getItemDetail(itemId: string): Promise<T> {
    // Load list data first (contains basic info — needed to validate item exists)
    await this.ensureListLoaded()
    const listItem = this.listCache.get(itemId)

    if (!listItem) {
      throw new Error(`${this.componentName} not found in list data: ${itemId}`)
    }

    // Fetch detail file and pricing in parallel
    const fetchDetail = async (): Promise<Partial<T>> => {
      const cached = this.detailCache.get(itemId)
      if (cached) return cached
      try {
        const response = await fetch(`${this.basePath}/details/${itemId}.json`)
        if (response.ok) {
          const data = await response.json() as Partial<T>
          this.detailCache.set(itemId, data)
          return data
        }
        return {}
      } catch (error) {
        console.warn(`Failed to load ${this.componentName} detail for ${itemId}:`, error)
        return {}
      }
    }

    const [detailData] = await Promise.all([
      fetchDetail(),
      this.ensurePricingLoaded(),
    ])

    const priceInfo = this.pricingCache.get(itemId)

    // Merge list data + detail data + pricing
    return {
      ...listItem,
      ...detailData,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null
    } as T
  }

  /**
   * Ensure list data is loaded and not stale
   */
  private async ensureListLoaded(): Promise<void> {
    const now = Date.now()

    // Check if list data is stale
    if (this.listCache.size > 0 && (now - this.listLoadTime) < this.LIST_TTL) {
      return // Use cached data
    }

    // Load both current and discontinued lists
    const [currentResponse, discontinuedResponse] = await Promise.all([
      fetch(`${this.basePath}/${this.componentName}-current-list.json`),
      fetch(`${this.basePath}/${this.componentName}-discontinued-list.json`)
    ])

    if (!currentResponse.ok && !discontinuedResponse.ok) {
      throw new Error(`Failed to load ${this.componentName} list data`)
    }

    // Update cache
    this.listCache.clear()
    this.currentIds.clear()
    this.discontinuedIds.clear()

    if (currentResponse.ok) {
      const currentList = await currentResponse.json() as T[]
      currentList.forEach(item => {
        this.listCache.set(item.id, item)
        this.currentIds.add(item.id)
      })
    }

    if (discontinuedResponse.ok) {
      const discontinuedList = await discontinuedResponse.json() as T[]
      discontinuedList.forEach(item => {
        this.listCache.set(item.id, item)
        this.discontinuedIds.add(item.id)
      })
    }

    this.listLoadTime = now
  }

  /**
   * Ensure pricing data is loaded and not stale
   */
  private async ensurePricingLoaded(): Promise<void> {
    const now = Date.now()

    // Check if pricing data is stale
    if (this.pricingCache.size > 0 && (now - this.pricingLoadTime) < this.PRICING_TTL) {
      return // Use cached data
    }

    // Load both current and discontinued pricing
    const [currentResponse, discontinuedResponse] = await Promise.all([
      fetch(`${this.basePath}/${this.componentName}-current-prices.json`),
      fetch(`${this.basePath}/${this.componentName}-discontinued-prices.json`)
    ])

    // Update cache
    this.pricingCache.clear()

    if (currentResponse.ok) {
      try {
        const currentData: PricingData = await currentResponse.json()
        if (currentData?.prices) {
          Object.entries(currentData.prices).forEach(([id, price]) => {
            this.pricingCache.set(id, price)
          })
        }
      } catch (error) {
        console.warn(`Failed to parse current pricing data:`, error)
      }
    }

    if (discontinuedResponse.ok) {
      try {
        const discontinuedData: PricingData = await discontinuedResponse.json()
        if (discontinuedData?.prices) {
          Object.entries(discontinuedData.prices).forEach(([id, price]) => {
            this.pricingCache.set(id, price)
          })
        }
      } catch (error) {
        console.warn(`Failed to parse discontinued pricing data:`, error)
      }
    }

    this.pricingLoadTime = now
  }

  /**
   * Get all items (current + discontinued) with pricing merged
   */
  async getAllItems(): Promise<T[]> {
    await Promise.all([this.ensureListLoaded(), this.ensurePricingLoaded()])
    return Array.from(this.listCache.values()).map(item => this.mergeWithPricing(item))
  }

  /**
   * Get current items only with pricing merged
   */
  async getCurrentItems(): Promise<T[]> {
    await Promise.all([this.ensureListLoaded(), this.ensurePricingLoaded()])
    return Array.from(this.listCache.values())
      .filter(item => this.currentIds.has(item.id))
      .map(item => this.mergeWithPricing(item))
  }

  /**
   * Get discontinued items only with pricing merged
   */
  async getDiscontinuedItems(): Promise<T[]> {
    await Promise.all([this.ensureListLoaded(), this.ensurePricingLoaded()])
    return Array.from(this.listCache.values())
      .filter(item => this.discontinuedIds.has(item.id))
      .map(item => this.mergeWithPricing(item))
  }

  private mergeWithPricing(item: T): T {
    const priceInfo = this.pricingCache.get(item.id)
    return {
      ...item,
      regularPrice: priceInfo?.regularPrice ?? null,
      currentPrice: priceInfo?.currentPrice ?? null,
    } as T
  }

  /**
   * Clear all caches
   */
  clearCache(): void {
    this.detailCache.clear()
    this.listCache.clear()
    this.currentIds.clear()
    this.discontinuedIds.clear()
    this.pricingCache.clear()
    this.listLoadTime = 0
    this.pricingLoadTime = 0
  }
}

/**
 * Create a singleton factory for a specific component
 */
export function createSingletonAdapter<T extends ComponentModel>(
  config: GenericDataAdapterConfig
): {
  getAdapter: () => GenericDataAdapter<T>
  resetAdapter: () => void
} {
  let instance: GenericDataAdapter<T> | null = null

  return {
    getAdapter: () => {
      instance ??= new GenericDataAdapter<T>(config)
      return instance
    },
    resetAdapter: () => {
      instance = null
    }
  }
}
